import React, { useState, useEffect, useCallback } from 'react';
import ArchivePage from './ArchivePage';

const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS_SHORT = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const DOT_COLORS = { default: '#a07ac4' };

const S = {
  page: { minHeight: '100vh', background: 'var(--bg)' },
  topbar: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 28px', borderBottom: '0.5px solid var(--border)',
    position: 'sticky', top: 0, background: 'var(--bg)', zIndex: 10,
  },
  topLeft: { display: 'flex', alignItems: 'center', gap: '24px' },
  logo: { fontFamily: 'var(--font-display)', fontSize: '18px', fontWeight: 300, fontStyle: 'italic', color: 'var(--accent)', letterSpacing: '-0.01em' },
  navBtn: { fontSize: '12px', color: 'var(--text-secondary)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 10px', borderRadius: 'var(--radius-sm)', transition: 'all 0.15s', letterSpacing: '0.04em' },
  navBtnActive: { fontSize: '12px', color: 'var(--accent)', background: 'var(--accent-dim)', border: 'none', cursor: 'pointer', padding: '4px 10px', borderRadius: 'var(--radius-sm)', letterSpacing: '0.04em' },
  userRow: { display: 'flex', alignItems: 'center', gap: '10px' },
  avatar: { width: 26, height: 26, borderRadius: '50%', border: '0.5px solid var(--border)' },
  userName: { fontSize: '12px', color: 'var(--text-secondary)' },
  logoutBtn: { fontSize: '11px', color: 'var(--text-tertiary)', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: 'var(--radius-sm)' },
  main: { padding: '24px 28px', maxWidth: '1140px', margin: '0 auto' },
  dayHeading: { fontFamily: 'var(--font-display)', fontSize: '32px', fontWeight: 300, fontStyle: 'italic', color: 'var(--text-primary)', marginBottom: '2px' },
  dateSub: { fontSize: '12px', color: 'var(--text-tertiary)', marginBottom: '24px', letterSpacing: '0.05em' },
  grid: { display: 'grid', gridTemplateColumns: '340px 1fr', gap: '16px', alignItems: 'start' },
  col: { display: 'flex', flexDirection: 'column', gap: '14px' },
  card: { background: 'var(--bg-card)', border: '0.5px solid var(--border)', borderRadius: 'var(--radius)', padding: '16px 18px' },
  cardTitle: { fontSize: '10px', color: 'var(--text-tertiary)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  badge: { fontSize: '10px', padding: '2px 8px', borderRadius: '20px', letterSpacing: '0.03em' },
  eventItem: { display: 'flex', gap: '10px', padding: '8px 10px', borderRadius: 'var(--radius-sm)', border: '0.5px solid var(--border)', marginBottom: '5px', transition: 'border-color 0.15s' },
  eventDot: { width: 7, height: 7, borderRadius: '50%', flexShrink: 0, marginTop: 5 },
  eventTime: { fontSize: '11px', color: 'var(--text-tertiary)', minWidth: 62, textAlign: 'right' },
  eventName: { fontSize: '13px', color: 'var(--text-primary)', flex: 1 },
  input: { flex: 1, height: 30, padding: '0 10px', fontSize: '12px', border: '0.5px solid var(--border)', borderRadius: 'var(--radius-sm)', background: 'var(--bg-input)', color: 'var(--text-primary)', outline: 'none', transition: 'border-color 0.15s' },
  select: { height: 30, padding: '0 6px', fontSize: '11px', border: '0.5px solid var(--border)', borderRadius: 'var(--radius-sm)', background: 'var(--bg-input)', color: 'var(--text-secondary)', outline: 'none' },
  addBtn: { height: 30, padding: '0 12px', fontSize: '11px', border: '0.5px solid var(--border)', borderRadius: 'var(--radius-sm)', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', transition: 'all 0.15s', letterSpacing: '0.03em', whiteSpace: 'nowrap' },
  taskItem: { display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '7px 8px', borderRadius: 'var(--radius-sm)', border: '0.5px solid transparent', transition: 'border-color 0.15s' },
  check: { width: 14, height: 14, borderRadius: '3px', border: '1px solid var(--border-hover)', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s', marginTop: 2 },
  itemText: { flex: 1, fontSize: '13px', color: 'var(--text-primary)', lineHeight: 1.4 },
  delBtn: { fontSize: '15px', color: 'var(--text-tertiary)', cursor: 'pointer', padding: '0 2px', lineHeight: 1, background: 'none', border: 'none', opacity: 0, transition: 'opacity 0.15s' },
  sectionEmpty: { fontSize: '12px', color: 'var(--text-tertiary)', padding: '12px 8px' },
  inputGroup: { display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' },
  progressWrap: { height: 2, background: 'var(--border)', borderRadius: 1, marginTop: 12 },
  syncNote: { fontSize: '11px', color: 'var(--text-tertiary)', marginTop: 8, fontStyle: 'italic' },
};

const priorityBadge = (p) => {
  if (p === 'high') return { background: 'var(--red-dim)', color: 'var(--red)', fontSize: '10px', padding: '1px 6px', borderRadius: '10px', flexShrink: 0 };
  if (p === 'low') return { background: 'var(--green-dim)', color: 'var(--green)', fontSize: '10px', padding: '1px 6px', borderRadius: '10px', flexShrink: 0 };
  return { background: 'var(--amber-dim)', color: 'var(--amber)', fontSize: '10px', padding: '1px 6px', borderRadius: '10px', flexShrink: 0 };
};

const catBadge = (c) => {
  if (c === 'personal') return { background: 'var(--green-dim)', color: 'var(--green)', fontSize: '10px', padding: '1px 6px', borderRadius: '10px', flexShrink: 0 };
  return { background: 'var(--blue-dim)', color: 'var(--blue)', fontSize: '10px', padding: '1px 6px', borderRadius: '10px', flexShrink: 0 };
};

function loadData(key, def) {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : def; } catch { return def; }
}
function saveData(key, val) {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch {}
}

function formatTime(dateStr) {
  const d = new Date(dateStr);
  let h = d.getHours(), m = d.getMinutes();
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${m.toString().padStart(2, '0')} ${ampm}`;
}

async function fetchTodayEvents(accessToken) {
  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0);
  const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
  const params = new URLSearchParams({
    timeMin: startOfDay.toISOString(),
    timeMax: endOfDay.toISOString(),
    singleEvents: 'true',
    orderBy: 'startTime',
    maxResults: '20',
  });
  const res = await fetch(`https://www.googleapis.com/calendar/v3/calendars/primary/events?${params}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err?.error?.message || `HTTP ${res.status}`);
  }
  const data = await res.json();
  return (data.items || [])
    .filter(ev => ev.status !== 'cancelled')
    .map(ev => ({
      name: ev.summary || '(no title)',
      time: ev.start?.dateTime ? formatTime(ev.start.dateTime) : 'all day',
      color: ev.colorId ? `#${['', 'a4bdfc','7ae7bf','dbadff','ff887c','fbd75b','ffb878','46d6db','e1e1e1','5484ed','51b749','dc2127'][ev.colorId] || 'a07ac4'}` : DOT_COLORS.default,
    }));
}

async function fetchGoogleTasks(accessToken) {
  // Fetch all task lists first
  const listsRes = await fetch('https://tasks.googleapis.com/tasks/v1/users/@me/lists?maxResults=20', {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (!listsRes.ok) throw new Error(`Tasks API ${listsRes.status}`);
  const listsData = await listsRes.json();
  const lists = listsData.items || [];

  // Fetch incomplete tasks from all lists
  const allTasks = [];
  for (const list of lists) {
    const tasksRes = await fetch(
      `https://tasks.googleapis.com/tasks/v1/lists/${list.id}/tasks?showCompleted=false&showHidden=false&maxResults=50`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    if (!tasksRes.ok) continue;
    const tasksData = await tasksRes.json();
    (tasksData.items || []).forEach(t => {
      if (t.status !== 'completed') {
        allTasks.push({
          id: `gtask-${t.id}`,
          text: t.title || '(untitled)',
          priority: 'med',
          category: 'work',
          done: false,
          doneAt: null,
          googleId: t.id,
          listId: list.id,
          fromGoogle: true,
          due: t.due || null,
        });
      }
    });
  }
  return allTasks;
}

async function completeGoogleTask(accessToken, listId, taskId) {
  await fetch(`https://tasks.googleapis.com/tasks/v1/lists/${listId}/tasks/${taskId}`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ status: 'completed' }),
  });
}

async function createGoogleTask(accessToken, title, listId = '@default') {
  const res = await fetch(`https://tasks.googleapis.com/tasks/v1/lists/${listId}/tasks`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ title }),
  });
  const data = await res.json();
  return data.id;
}

export default function Dashboard({ user, accessToken, onLogout }) {
  const now = new Date();
  const [page, setPage] = useState('dashboard');

  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState(null);

  const [tasks, setTasks] = useState(() => loadData('decodif_tasks_v3', []));
  const [tasksLoading, setTasksLoading] = useState(false);
  const [tasksSynced, setTasksSynced] = useState(false);

  const [decisions, setDecisions] = useState(() => loadData('decodif_decisions', []));
  const [followups, setFollowups] = useState(() => loadData('decodif_followups', []));

  const [taskInput, setTaskInput] = useState('');
  const [taskPriority, setTaskPriority] = useState('med');
  const [taskCategory, setTaskCategory] = useState('work');
  const [decisionInput, setDecisionInput] = useState('');
  const [decisionDate, setDecisionDate] = useState('');
  const [followupName, setFollowupName] = useState('');
  const [followupProject, setFollowupProject] = useState('');
  const [followupNote, setFollowupNote] = useState('');

  useEffect(() => saveData('decodif_tasks_v3', tasks), [tasks]);
  useEffect(() => saveData('decodif_decisions', decisions), [decisions]);
  useEffect(() => saveData('decodif_followups', followups), [followups]);

  // Fetch calendar events
  useEffect(() => {
    if (!accessToken) return;
    setEventsLoading(true);
    setEventsError(null);
    fetchTodayEvents(accessToken)
      .then(evs => { setEvents(evs); setEventsLoading(false); })
      .catch(err => { setEventsError(`Could not load calendar: ${err.message}`); setEventsLoading(false); });
  }, [accessToken]);

  // Fetch Google Tasks (only once per session to avoid overwriting local edits)
  useEffect(() => {
    if (!accessToken || tasksSynced) return;
    setTasksLoading(true);
    fetchGoogleTasks(accessToken)
      .then(googleTasks => {
        setTasks(prev => {
          // Merge: keep local-only tasks, update/add Google tasks
          const localOnly = prev.filter(t => !t.fromGoogle);
          return [...localOnly, ...googleTasks];
        });
        setTasksSynced(true);
        setTasksLoading(false);
      })
      .catch(() => {
        // Fail silently — local tasks still work
        setTasksLoading(false);
      });
  }, [accessToken, tasksSynced]);

  const addTask = useCallback(async () => {
    const text = taskInput.trim();
    if (!text) return;
    const newTask = { id: Date.now(), text, priority: taskPriority, category: taskCategory, done: false, doneAt: null, fromGoogle: false };
    setTasks(t => [...t, newTask]);
    setTaskInput('');
    // Also create in Google Tasks if connected
    if (accessToken) {
      try {
        const googleId = await createGoogleTask(accessToken, text);
        setTasks(t => t.map(task => task.id === newTask.id ? { ...task, googleId, listId: '@default', fromGoogle: true } : task));
      } catch {}
    }
  }, [taskInput, taskPriority, taskCategory, accessToken]);

  const completeTask = async (id) => {
    const task = tasks.find(t => t.id === id);
    if (!task) return;
    setTasks(ts => ts.map(t => t.id === id ? { ...t, done: true, doneAt: new Date().toISOString() } : t));
    if (accessToken && task.googleId && task.listId) {
      try { await completeGoogleTask(accessToken, task.listId, task.googleId); } catch {}
    }
  };
  const deleteTask = (id) => setTasks(ts => ts.filter(t => t.id !== id));

  const addDecision = () => {
    const title = decisionInput.trim();
    if (!title) return;
    setDecisions(d => [...d, { id: Date.now(), title, dueDate: decisionDate, done: false, doneAt: null }]);
    setDecisionInput(''); setDecisionDate('');
  };
  const completeDecision = (id) => setDecisions(ds => ds.map(d => d.id === id ? { ...d, done: true, doneAt: new Date().toISOString() } : d));
  const deleteDecision = (id) => setDecisions(ds => ds.filter(d => d.id !== id));

  const addFollowup = () => {
    const name = followupName.trim();
    if (!name) return;
    setFollowups(f => [...f, { id: Date.now(), name, project: followupProject.trim(), note: followupNote.trim(), done: false, doneAt: null }]);
    setFollowupName(''); setFollowupProject(''); setFollowupNote('');
  };
  const completeFollowup = (id) => setFollowups(fs => fs.map(f => f.id === id ? { ...f, done: true, doneAt: new Date().toISOString() } : f));
  const deleteFollowup = (id) => setFollowups(fs => fs.filter(f => f.id !== id));

  const openTasks = tasks.filter(t => !t.done);
  const openDecisions = decisions.filter(d => !d.done);
  const openFollowups = followups.filter(f => !f.done);
  const totalOpen = openTasks.length + openDecisions.length + openFollowups.length;
  const totalDone = tasks.filter(t => t.done).length + decisions.filter(d => d.done).length + followups.filter(f => f.done).length;
  const pct = (totalOpen + totalDone) > 0 ? Math.round((totalDone / (totalOpen + totalDone)) * 100) : 0;

  const formatDue = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const diff = Math.ceil((d - new Date()) / 86400000);
    if (diff < 0) return 'overdue';
    if (diff === 0) return 'today';
    if (diff === 1) return 'tomorrow';
    return `${DAYS_SHORT[d.getDay()]} ${d.getDate()}`;
  };
  const isDueUrgent = (dateStr) => {
    if (!dateStr) return false;
    return Math.ceil((new Date(dateStr) - new Date()) / 86400000) <= 2;
  };

  if (page === 'archive') {
    return <ArchivePage tasks={tasks} decisions={decisions} followups={followups} onBack={() => setPage('dashboard')} user={user} onLogout={onLogout} />;
  }

  const CheckBox = ({ onCheck }) => (
    <div style={S.check} onClick={onCheck}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.background = 'var(--accent-dim)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-hover)'; e.currentTarget.style.background = 'transparent'; }} />
  );

  const DelBtn = ({ onDel }) => (
    <button style={S.delBtn}
      onMouseEnter={e => e.currentTarget.style.opacity = 1}
      onMouseLeave={e => e.currentTarget.style.opacity = 0}
      onClick={onDel}>×</button>
  );

  const HoverBtn = ({ style, onClick, children }) => (
    <button style={style} onClick={onClick}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-hover)'; e.currentTarget.style.background = 'var(--bg-hover)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'transparent'; }}>
      {children}
    </button>
  );

  return (
    <div style={S.page}>
      <div style={S.topbar}>
        <div style={S.topLeft}>
          <div style={S.logo}>decodification</div>
          <button style={S.navBtnActive}>today</button>
          <button style={S.navBtn} onClick={() => setPage('archive')}
            onMouseEnter={e => { e.currentTarget.style.color = 'var(--text-primary)'; e.currentTarget.style.background = 'var(--bg-hover)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.background = 'none'; }}>
            archive
          </button>
        </div>
        <div style={S.userRow}>
          {user.picture && <img src={user.picture} alt="" style={S.avatar} referrerPolicy="no-referrer" />}
          <span style={S.userName}>{user.name}</span>
          <button style={S.logoutBtn} onClick={onLogout}
            onMouseEnter={e => e.currentTarget.style.color = 'var(--text-secondary)'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--text-tertiary)'}>sign out</button>
        </div>
      </div>

      <div style={S.main}>
        <div style={S.dayHeading}>{DAYS[now.getDay()]}</div>
        <div style={S.dateSub}>{MONTHS[now.getMonth()]} {now.getDate()}, {now.getFullYear()} · {totalOpen} open · {pct}% complete today</div>

        <div style={S.grid}>
          {/* LEFT COLUMN */}
          <div style={S.col}>

            {/* Calendar */}
            <div style={S.card}>
              <div style={S.cardTitle}>
                <span>today's schedule</span>
                <span style={{ ...S.badge, background: 'var(--blue-dim)', color: 'var(--blue)' }}>
                  {eventsLoading ? 'syncing...' : `${events.length} events`}
                </span>
              </div>
              {eventsLoading && <div style={S.sectionEmpty}>loading calendar...</div>}
              {eventsError && <div style={{ ...S.sectionEmpty, color: 'var(--red)' }}>{eventsError}</div>}
              {!eventsLoading && !eventsError && events.length === 0 && (
                <div style={S.sectionEmpty}>no events scheduled today</div>
              )}
              {!eventsLoading && !eventsError && events.map((ev, i) => (
                <div key={i} style={S.eventItem}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border-hover)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
                  <div style={{ ...S.eventDot, background: ev.color }} />
                  <span style={S.eventName}>{ev.name}</span>
                  <span style={S.eventTime}>{ev.time}</span>
                </div>
              ))}
            </div>

            {/* Decisions & Deadlines */}
            <div style={S.card}>
              <div style={S.cardTitle}>
                <span>decisions & deadlines</span>
                <span style={{ ...S.badge, background: openDecisions.some(d => isDueUrgent(d.dueDate)) ? 'var(--red-dim)' : 'var(--bg-hover)', color: openDecisions.some(d => isDueUrgent(d.dueDate)) ? 'var(--red)' : 'var(--text-secondary)' }}>
                  {openDecisions.length} pending
                </span>
              </div>
              {openDecisions.length === 0 && <div style={S.sectionEmpty}>no pending decisions</div>}
              {openDecisions.map(d => (
                <div key={d.id} style={{ ...S.taskItem, border: '0.5px solid var(--border)', marginBottom: 5 }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border-hover)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
                  <CheckBox onCheck={() => completeDecision(d.id)} />
                  <span style={S.itemText}>{d.title}</span>
                  {d.dueDate && (
                    <span style={{ fontSize: '11px', color: isDueUrgent(d.dueDate) ? 'var(--red)' : 'var(--text-tertiary)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                      {formatDue(d.dueDate)}
                    </span>
                  )}
                  <DelBtn onDel={() => deleteDecision(d.id)} />
                </div>
              ))}
              <div style={S.inputGroup}>
                <input style={{ ...S.input, flex: 2 }} placeholder="Add decision or deadline..."
                  value={decisionInput} onChange={e => setDecisionInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addDecision()}
                  onFocus={e => e.target.style.borderColor = 'var(--border-hover)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                <input style={{ ...S.input, flex: 1 }} type="date" value={decisionDate}
                  onChange={e => setDecisionDate(e.target.value)}
                  onFocus={e => e.target.style.borderColor = 'var(--border-hover)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                <HoverBtn style={S.addBtn} onClick={addDecision}>add</HoverBtn>
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div style={S.col}>

            {/* Tasks */}
            <div style={S.card}>
              <div style={S.cardTitle}>
                <span>tasks</span>
                <span style={{ ...S.badge, background: 'var(--accent-dim)', color: 'var(--accent)' }}>
                  {tasksLoading ? 'syncing...' : `${openTasks.length} open`}
                </span>
              </div>
              <div style={{ maxHeight: 300, overflowY: 'auto', marginBottom: 4 }}>
                {tasksLoading && <div style={S.sectionEmpty}>syncing from Google Tasks...</div>}
                {!tasksLoading && openTasks.length === 0 && <div style={S.sectionEmpty}>all clear</div>}
                {!tasksLoading && openTasks
                  .sort((a, b) => ({ high: 0, med: 1, low: 2 }[a.priority] - { high: 0, med: 1, low: 2 }[b.priority]))
                  .map(task => (
                    <div key={task.id} style={S.taskItem}
                      onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border)'}
                      onMouseLeave={e => e.currentTarget.style.borderColor = 'transparent'}>
                      <CheckBox onCheck={() => completeTask(task.id)} />
                      <span style={S.itemText}>{task.text}</span>
                      <span style={catBadge(task.category)}>{task.category}</span>
                      <span style={priorityBadge(task.priority)}>{task.priority}</span>
                      <DelBtn onDel={() => deleteTask(task.id)} />
                    </div>
                  ))}
              </div>
              <div style={S.inputGroup}>
                <input style={{ ...S.input, flex: 2 }} placeholder="Add a task..."
                  value={taskInput} onChange={e => setTaskInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addTask()}
                  onFocus={e => e.target.style.borderColor = 'var(--border-hover)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                <select style={S.select} value={taskCategory} onChange={e => setTaskCategory(e.target.value)}>
                  <option value="work">work</option>
                  <option value="personal">personal</option>
                </select>
                <select style={S.select} value={taskPriority} onChange={e => setTaskPriority(e.target.value)}>
                  <option value="high">high</option>
                  <option value="med">med</option>
                  <option value="low">low</option>
                </select>
                <HoverBtn style={S.addBtn} onClick={addTask}>add</HoverBtn>
              </div>
              <div style={S.progressWrap}>
                <div style={{ height: '100%', width: `${pct}%`, background: 'var(--accent)', borderRadius: 1, transition: 'width 0.4s' }} />
              </div>
              {tasksSynced && <div style={S.syncNote}>synced with google tasks · new tasks added here sync back automatically</div>}
            </div>

            {/* Follow-ups */}
            <div style={S.card}>
              <div style={S.cardTitle}>
                <span>follow-ups</span>
                <span style={{ ...S.badge, background: 'var(--bg-hover)', color: 'var(--text-secondary)' }}>{openFollowups.length} pending</span>
              </div>
              <div style={{ maxHeight: 260, overflowY: 'auto', marginBottom: 4 }}>
                {openFollowups.length === 0 && <div style={S.sectionEmpty}>no pending follow-ups</div>}
                {openFollowups.map(f => (
                  <div key={f.id} style={{ padding: '9px 10px', borderRadius: 'var(--radius-sm)', border: '0.5px solid var(--border)', marginBottom: 5, transition: 'border-color 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border-hover)'}
                    onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <CheckBox onCheck={() => completeFollowup(f.id)} />
                      <span style={{ fontSize: '13px', color: 'var(--text-primary)', flex: 1 }}>{f.name}</span>
                      {f.project && <span style={{ fontSize: '10px', padding: '1px 7px', borderRadius: '10px', background: 'var(--bg-hover)', color: 'var(--text-secondary)', flexShrink: 0 }}>{f.project}</span>}
                      <DelBtn onDel={() => deleteFollowup(f.id)} />
                    </div>
                    {f.note && <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginTop: 4, paddingLeft: 22 }}>{f.note}</div>}
                  </div>
                ))}
              </div>
              <div style={S.inputGroup}>
                <input style={S.input} placeholder="Name..."
                  value={followupName} onChange={e => setFollowupName(e.target.value)}
                  onFocus={e => e.target.style.borderColor = 'var(--border-hover)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                <input style={S.input} placeholder="Project / team..."
                  value={followupProject} onChange={e => setFollowupProject(e.target.value)}
                  onFocus={e => e.target.style.borderColor = 'var(--border-hover)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border)'} />
              </div>
              <div style={{ ...S.inputGroup, marginTop: 6 }}>
                <input style={{ ...S.input, flex: 3 }} placeholder="Context or note..."
                  value={followupNote} onChange={e => setFollowupNote(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addFollowup()}
                  onFocus={e => e.target.style.borderColor = 'var(--border-hover)'}
                  onBlur={e => e.target.style.borderColor = 'var(--border)'} />
                <HoverBtn style={S.addBtn} onClick={addFollowup}>add</HoverBtn>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
